
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertCircle } from "lucide-react";
import { Lead } from "@/types";
import { CSVMapperProps, ColumnMapping } from "./types";
import { MappingForm } from "./MappingForm";
import { CSVPreview } from "./CSVPreview";
import { useCSVProcessing } from "./CSVProcessing";

const CSVMapper = ({ file, onCancel, onComplete, dealerId }: CSVMapperProps) => {
  const [previewData, setPreviewData] = useState<string[][]>([]);
  const [headers, setHeaders] = useState<string[]>([]);
  const [columnMapping, setColumnMapping] = useState<ColumnMapping>({});
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { parseCSVPreview, processCSVFile } = useCSVProcessing();

  const handleMappingChange = (field: string, columnHeader: string) => {
    setColumnMapping((prev) => ({
      ...prev,
      [field]: columnHeader,
    }));
  };

  const processFile = () => {
    // Check if all required fields are mapped
    const requiredFields = [
      { key: "name", label: "Name" },
      { key: "email", label: "Email" },
      { key: "phone", label: "Phone" },
    ];
    
    const missingFields = requiredFields
      .filter(field => !columnMapping[field.key])
      .map(field => field.label);
    
    if (missingFields.length > 0) {
      const errorMsg = `Please map these fields: ${missingFields.join(", ")}`;
      setError(errorMsg);
      return;
    }

    setIsProcessing(true);
    setError(null);

    processCSVFile(
      file,
      columnMapping,
      dealerId,
      (leads) => {
        onComplete(leads);
        setIsProcessing(false);
      },
      (errorMessage) => {
        setError(errorMessage);
        setIsProcessing(false);
      }
    );
  };

  useEffect(() => {
    if (file) {
      setError(null);
      parseCSVPreview(
        file,
        (headers, previewData) => {
          setHeaders(headers);
          setPreviewData(previewData);
          
          // Try to auto-map columns based on header names
          const initialMapping: ColumnMapping = {};
          headers.forEach((header) => {
            const headerLower = header.toLowerCase().trim();
            
            if (headerLower.includes("name")) {
              initialMapping["name"] = header;
            } else if (headerLower.includes("email")) {
              initialMapping["email"] = header;
            } else if (headerLower.includes("phone") || headerLower.includes("mobile")) {
              initialMapping["phone"] = header;
            } else if (headerLower.includes("note") || headerLower.includes("comment")) {
              initialMapping["meta_notes"] = header;
            }
          });
          
          setColumnMapping(initialMapping);
        },
        (errorMessage) => {
          setError(errorMessage);
        }
      );
    }
  }, [file]);

  if (headers.length === 0) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col items-center justify-center py-8">
            <div className="spinner mb-4" />
            <p className="text-gray-500">Analyzing CSV file...</p>
            {error && (
              <Alert variant="destructive" className="mt-4">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Error analyzing CSV</AlertTitle>
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="airbnb-card">
      <CardHeader>
        <CardTitle className="text-2xl font-bold">Map CSV Columns</CardTitle>
        {error && (
          <Alert variant="destructive" className="mt-2">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
      </CardHeader>
      <CardContent>
        <MappingForm 
          headers={headers}
          columnMapping={columnMapping}
          onMappingChange={handleMappingChange}
        />
        
        <CSVPreview headers={headers} previewData={previewData} />
      </CardContent>
      <CardFooter className="flex justify-between">
        <Button variant="outline" onClick={onCancel} disabled={isProcessing}>
          Cancel
        </Button>
        <Button onClick={processFile} disabled={isProcessing}>
          {isProcessing ? "Processing..." : "Import Leads"}
        </Button>
      </CardFooter>
    </Card>
  );
};

export default CSVMapper;
